/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Geozone Category
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new geozone category in the organization.
 */

import { useFormik } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateOrganizationGeozoneCategoryMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import FormActionButtons from "../../../components/admin/formActionButtons";
import { AdminFormFieldInput } from "../../../components/admin/formFields";
import FTModal from "../../../components/common/FTModal";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  formInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";

const AdminsGeozoneCategoriesCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories.create_new",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // user mutation
  const [createOrgGeozoneCategoryAPITrigger] =
    useCreateOrganizationGeozoneCategoryMutation();

  const hideModal = () => {
    dispatch(
      setModalsData({ ...modalsState, showCreateGeozoneCategory: false })
    );
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    validationSchema: YupValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // if(APP_CONFIG.TOASTS.INFO) toast.info(t('loginScreen.toast.logging_you_in'));
      createOrgGeozoneCategoryAPITrigger({
        // account_id: `${listingQueryParams.users.accountid}`,
        categoryid: values.geozonecategoryid,
        displayname: values.displayname,
        description: values.description,
        notes: values.notes,
      })
        .unwrap()
        .then((data) => {
          if (!!data?.categoryid) {
            dispatch(
              setModalsData({
                ...modalsState,
                showCreateGeozoneCategory: false,
              })
            );
            toast.success(t("toast.create_success"), { autoClose: 10000 });
            navigate(
              `${routeUrls.dashboardChildren.adminChildren.geozoneCategories}/${data?.categoryid}`,
              { state: { new: true } }
            );
          } else {
            toast.error(t("toast.create_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.create_failed")];
          toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
  } = formik;
  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("submit_button"),
              variant: "primary",
              disabled: !isFormValid || isSubmitting /* || isLoading*/,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isSubmitting /* || isLoading*/,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isSubmitting]);
  return (
    <FTModal
      visible={modalsState.showCreateGeozoneCategory}
      heading={t("heading")}
      onHide={hideModal}
      disableOverlay={true}
    >
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t("sub_heading")}
        </p>

        <AdminFormFieldInput
          label={t("geozonecategory_id")}
          type="text"
          id="GeozoneCategoryId"
          name="geozonecategoryid"
          placeholder={t("geozonecategory_id_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.geozonecategoryid}
          touched={touched.geozonecategoryid}
          error={errors.geozonecategoryid}
        />

        <AdminFormFieldInput
          label={t("displayname")}
          type="text"
          id="displayname"
          name="displayname"
          placeholder={t("displayname_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.displayname}
          touched={touched.displayname}
          error={errors.displayname}
        />

        <AdminFormFieldInput
          label={t("description")}
          type="text"
          id="description"
          name="description"
          placeholder={t("description_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
          touched={touched.description}
          error={errors.description}
        />

        <AdminFormFieldInput
          label={t("notes")}
          type="text"
          id="notes"
          name="notes"
          placeholder={t("notes_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.notes}
          touched={touched.notes}
          error={errors.notes}
        />

        {MemoizedFormActionButtons}
      </form>
    </FTModal>
  );
};
export default AdminsGeozoneCategoriesCreateNew;
